import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../lib/firebase";
import "./FocusResetDashboard.css";

const STORAGE_KEY = "contract-dashboard-v1";
const CONTRACT_START_KEY = "2026-06-01";

const TARGETS = {
  swim: 1,
  bike: 3,
  writingSessions: 2,
  words: 1500,
};

const defaultWeek = {
  swim: 0,
  bike: 0,
  writingSessions: 0,
  words: 0,
  completedAt: null,
};

const defaultState = {
  weeks: {},
};

const PRIMARY_COLLECTION = "goals";
const PRIMARY_STATE_FIELD = "focusResetContractState";
const PRIMARY_UPDATED_FIELD = "focusResetContractUpdatedAt";
const LEGACY_COLLECTION = "focusResetDashboard";
const LEGACY_STATE_FIELD = "contractState";
const LEGACY_UPDATED_FIELD = "contractUpdatedAt";

const getAllowedUsers = () => {
  const usersString = process.env.REACT_APP_ALLOWED_USERS || "";
  const users = {};

  usersString.split(",").forEach((pair) => {
    const [username, password] = pair.split(":");
    if (username && password) {
      users[password.trim()] = username.trim();
    }
  });

  return users;
};

const parseDateKey = (key) => new Date(`${key}T00:00:00`);

const dateToKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const startOfWeekMonday = (date) => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  const day = (copy.getDay() + 6) % 7;
  copy.setDate(copy.getDate() - day);
  return copy;
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getWeekData = (weeks, weekKey) => ({
  ...defaultWeek,
  ...(weeks[weekKey] || {}),
});

const goalsMet = (week) =>
  week.swim >= TARGETS.swim &&
  week.bike >= TARGETS.bike &&
  week.writingSessions >= TARGETS.writingSessions &&
  week.words >= TARGETS.words;

const percent = (value, max) => Math.round((Math.min(value, max) / max) * 100);

const formatDateLabel = (date) =>
  date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

export default function FocusResetDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [state, setState] = useState(defaultState);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [authError, setAuthError] = useState("");
  const [cloudReady, setCloudReady] = useState(false);
  const [cloudStatus, setCloudStatus] = useState("");
  const [wordsToAdd, setWordsToAdd] = useState("250");

  const skipNextCloudSaveRef = useRef(false);

  useEffect(() => {
    const key = searchParams.get("key");
    const allowedUsers = getAllowedUsers();

    if (key && allowedUsers[key]) {
      setIsAuthorized(true);
      setCurrentUser(allowedUsers[key]);
      setAuthError("");
      return;
    }

    setIsAuthorized(false);
    setCurrentUser(null);
  }, [searchParams]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setState({
          ...defaultState,
          ...parsed,
          weeks: { ...(parsed.weeks || {}) },
        });
      }
    } catch {
      setState(defaultState);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    if (!isAuthorized || !currentUser) return;

    if (!db) {
      setCloudReady(true);
      setCloudStatus("Cloud sync unavailable");
      return;
    }

    let mounted = true;

    const loadCloud = async () => {
      setCloudStatus("Loading cloud data...");
      try {
        const primaryRef = doc(db, PRIMARY_COLLECTION, currentUser);
        const primarySnap = await getDoc(primaryRef);
        if (!mounted) return;

        let remote = primarySnap.data()?.[PRIMARY_STATE_FIELD];

        // Backward compatibility: try legacy read only if primary field is missing.
        // Some projects intentionally block this collection in rules.
        if (!remote || typeof remote !== "object") {
          try {
            const legacyRef = doc(db, LEGACY_COLLECTION, currentUser);
            const legacySnap = await getDoc(legacyRef);
            remote = legacySnap.data()?.[LEGACY_STATE_FIELD];
          } catch (legacyReadError) {
            // Ignore legacy permission-denied and continue with primary path.
            if (legacyReadError?.code !== "permission-denied") {
              throw legacyReadError;
            }
          }
        }

        if (remote && typeof remote === "object") {
          skipNextCloudSaveRef.current = true;
          setState({
            ...defaultState,
            ...remote,
            weeks: { ...(remote.weeks || {}) },
          });
        }
        setCloudStatus("Cloud sync active");
      } catch (error) {
        const code = error?.code ? ` (${error.code})` : "";
        if (mounted) setCloudStatus(`Cloud load failed${code}`);
      } finally {
        if (mounted) setCloudReady(true);
      }
    };

    loadCloud();

    return () => {
      mounted = false;
    };
  }, [isAuthorized, currentUser]);

  useEffect(() => {
    if (!isAuthorized || !currentUser || !cloudReady || !db) return;
    if (skipNextCloudSaveRef.current) {
      skipNextCloudSaveRef.current = false;
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        setCloudStatus("Saving...");
        const primaryRef = doc(db, PRIMARY_COLLECTION, currentUser);
        await setDoc(
          primaryRef,
          {
            [PRIMARY_STATE_FIELD]: state,
            [PRIMARY_UPDATED_FIELD]: Date.now(),
          },
          { merge: true },
        );
        setCloudStatus("Saved");
      } catch (primaryError) {
        // Backward compatibility fallback for existing legacy setups.
        try {
          const legacyRef = doc(db, LEGACY_COLLECTION, currentUser);
          await setDoc(
            legacyRef,
            {
              [LEGACY_STATE_FIELD]: state,
              [LEGACY_UPDATED_FIELD]: Date.now(),
            },
            { merge: true },
          );
          setCloudStatus("Saved (legacy)");
        } catch (legacyError) {
          const code = legacyError?.code || primaryError?.code;
          setCloudStatus(code ? `Save failed (${code})` : "Save failed");
        }
      }
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [state, isAuthorized, currentUser, cloudReady]);

  const now = new Date();
  const programStart = useMemo(() => parseDateKey(CONTRACT_START_KEY), []);
  const hasStarted = now >= programStart;

  const currentWeekStart = (() => {
    if (!hasStarted) return null;
    const monday = startOfWeekMonday(now);
    return monday < programStart ? programStart : monday;
  })();

  const currentWeekKey = currentWeekStart ? dateToKey(currentWeekStart) : null;
  const currentWeek = currentWeekKey
    ? getWeekData(state.weeks, currentWeekKey)
    : defaultWeek;
  const displayWeekStart = currentWeekStart || programStart;
  const previewMode = !hasStarted;

  const allMet = goalsMet(currentWeek);
  const isCompleted = Boolean(currentWeek.completedAt);
  const canComplete = hasStarted && allMet && !isCompleted;

  const daysUntilStart = Math.max(
    0,
    Math.ceil((programStart.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)),
  );

  const setMetric = (metric, value) => {
    if (!currentWeekKey) return;
    const max = TARGETS[metric];
    const next = clamp(value, 0, max);

    setState((prev) => {
      const week = getWeekData(prev.weeks, currentWeekKey);
      return {
        ...prev,
        weeks: {
          ...prev.weeks,
          [currentWeekKey]: {
            ...week,
            [metric]: next,
          },
        },
      };
    });
  };

  const addWords = () => {
    const parsed = Number(wordsToAdd);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    setMetric("words", currentWeek.words + Math.floor(parsed));
  };

  const completeWeek = () => {
    if (!canComplete || !currentWeekKey) return;
    setState((prev) => {
      const week = getWeekData(prev.weeks, currentWeekKey);
      return {
        ...prev,
        weeks: {
          ...prev.weeks,
          [currentWeekKey]: {
            ...week,
            completedAt: Date.now(),
          },
        },
      };
    });
  };

  const historyRows = useMemo(() => {
    if (!hasStarted || !currentWeekStart) return [];

    const rows = [];
    for (
      let cursor = new Date(programStart);
      cursor <= currentWeekStart;
      cursor = addDays(cursor, 7)
    ) {
      const weekKey = dateToKey(cursor);
      const week = getWeekData(state.weeks, weekKey);
      const rowWeekEnd = addDays(cursor, 6);
      const met = goalsMet(week);

      let status = "In progress";
      if (week.completedAt) {
        status = "Completed";
      } else if (cursor < currentWeekStart) {
        status = "Missed";
      } else if (met) {
        status = "Ready to complete";
      }

      rows.push({
        weekKey,
        label: `${formatDateLabel(cursor)} - ${formatDateLabel(rowWeekEnd)}`,
        week,
        status,
      });
    }

    return rows.reverse();
  }, [hasStarted, currentWeekStart, programStart, state.weeks]);

  const handleUnlock = (e) => {
    e.preventDefault();
    const key = passwordInput.trim();
    const allowedUsers = getAllowedUsers();
    if (key && allowedUsers[key]) {
      setSearchParams({ key });
      setAuthError("");
      setPasswordInput("");
      return;
    }
    setAuthError("Invalid password");
  };

  if (!isAuthorized) {
    return (
      <div className="contract-page">
        <div className="contract-shell">
          <div className="contract-lock-card">
            <h1>Locked Dashboard</h1>
            <p>Enter the admin password.</p>

            <form onSubmit={handleUnlock} className="contract-lock-form">
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="Enter password"
                autoFocus
              />

              {authError ? (
                <div className="contract-lock-error">{authError}</div>
              ) : null}

              <div className="contract-actions">
                <button
                  type="button"
                  className="ghost"
                  onClick={() => navigate("/admin")}
                >
                  Back to Admin
                </button>
                <button type="submit">Unlock</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="contract-page">
      <div className="contract-shell">
        <header className="contract-header">
          <h1>Weekly Contract Tracker</h1>
          <p>Start date: Jun 1, 2026. Week runs Monday to Sunday midnight.</p>
          <p className="cloud-status">{cloudStatus}</p>
        </header>

        {previewMode && (
          <section className="contract-card">
            <h2>Program not started yet</h2>
            <p>
              Starts on Jun 1, 2026. {daysUntilStart} day
              {daysUntilStart === 1 ? "" : "s"} remaining.
            </p>
          </section>
        )}

        <>
          <section className="contract-card">
            <h2>
              {previewMode ? "First Contract Week" : "Current Week"}:{" "}
              {formatDateLabel(displayWeekStart)} -{" "}
              {formatDateLabel(addDays(displayWeekStart, 6))}
            </h2>
            <p>
              Deadline: Sunday at 11:59 PM
              {previewMode
                ? " (preview mode; tracking unlocks on start date)"
                : ""}
            </p>

            <div className="goal-list">
              <div className="goal-row">
                <div>
                  <strong>Swim</strong>
                  <span>
                    {currentWeek.swim}/{TARGETS.swim}
                  </span>
                </div>
                <div className="counter-controls">
                  <button
                    disabled={previewMode}
                    onClick={() => setMetric("swim", currentWeek.swim - 1)}
                  >
                    -
                  </button>
                  <button
                    disabled={previewMode}
                    onClick={() => setMetric("swim", currentWeek.swim + 1)}
                  >
                    +1
                  </button>
                </div>
              </div>

              <div className="goal-row">
                <div>
                  <strong>Bike</strong>
                  <span>
                    {currentWeek.bike}/{TARGETS.bike}
                  </span>
                </div>
                <div className="counter-controls">
                  <button
                    disabled={previewMode}
                    onClick={() => setMetric("bike", currentWeek.bike - 1)}
                  >
                    -
                  </button>
                  <button
                    disabled={previewMode}
                    onClick={() => setMetric("bike", currentWeek.bike + 1)}
                  >
                    +1
                  </button>
                </div>
              </div>

              <div className="goal-row">
                <div>
                  <strong>Writing sessions</strong>
                  <span>
                    {currentWeek.writingSessions}/{TARGETS.writingSessions}
                  </span>
                </div>
                <div className="counter-controls">
                  <button
                    disabled={previewMode}
                    onClick={() =>
                      setMetric(
                        "writingSessions",
                        currentWeek.writingSessions - 1,
                      )
                    }
                  >
                    -
                  </button>
                  <button
                    disabled={previewMode}
                    onClick={() =>
                      setMetric(
                        "writingSessions",
                        currentWeek.writingSessions + 1,
                      )
                    }
                  >
                    +1
                  </button>
                </div>
              </div>

              <div className="goal-row words-row">
                <div>
                  <strong>Words</strong>
                  <span>
                    {currentWeek.words}/{TARGETS.words}
                  </span>
                </div>
                <div className="words-controls">
                  <button
                    disabled={previewMode}
                    onClick={() => setMetric("words", currentWeek.words - 100)}
                  >
                    -100
                  </button>
                  <input
                    type="number"
                    min="1"
                    disabled={previewMode}
                    value={wordsToAdd}
                    onChange={(e) => setWordsToAdd(e.target.value)}
                  />
                  <button disabled={previewMode} onClick={addWords}>
                    Add
                  </button>
                </div>
              </div>
            </div>

            <div className="progress-grid">
              <div className="progress-item">
                <span>Swim</span>
                <div className="track">
                  <div
                    className="fill"
                    style={{
                      width: `${percent(currentWeek.swim, TARGETS.swim)}%`,
                    }}
                  />
                </div>
              </div>
              <div className="progress-item">
                <span>Bike</span>
                <div className="track">
                  <div
                    className="fill"
                    style={{
                      width: `${percent(currentWeek.bike, TARGETS.bike)}%`,
                    }}
                  />
                </div>
              </div>
              <div className="progress-item">
                <span>Sessions</span>
                <div className="track">
                  <div
                    className="fill"
                    style={{
                      width: `${percent(currentWeek.writingSessions, TARGETS.writingSessions)}%`,
                    }}
                  />
                </div>
              </div>
              <div className="progress-item">
                <span>Words</span>
                <div className="track">
                  <div
                    className="fill"
                    style={{
                      width: `${percent(currentWeek.words, TARGETS.words)}%`,
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="complete-row">
              <button
                className="complete-btn"
                onClick={completeWeek}
                disabled={previewMode || !canComplete}
              >
                {isCompleted ? "Week Completed" : "Complete Week"}
              </button>
              {previewMode && <span>Preview only until Jun 1.</span>}
              {!previewMode && !allMet && (
                <span>Hit all four goals to complete this week.</span>
              )}
              {isCompleted && (
                <span>
                  Completed {new Date(currentWeek.completedAt).toLocaleString()}
                </span>
              )}
            </div>
          </section>

          <section className="contract-card">
            <h2>Week History</h2>
            {previewMode && (
              <p>History will begin once week one starts on Jun 1.</p>
            )}
            <div className="history-table-wrap">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Week</th>
                    <th>Swim</th>
                    <th>Bike</th>
                    <th>Sessions</th>
                    <th>Words</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {historyRows.map((row) => (
                    <tr key={row.weekKey}>
                      <td>{row.label}</td>
                      <td>
                        {row.week.swim}/{TARGETS.swim}
                      </td>
                      <td>
                        {row.week.bike}/{TARGETS.bike}
                      </td>
                      <td>
                        {row.week.writingSessions}/{TARGETS.writingSessions}
                      </td>
                      <td>
                        {row.week.words}/{TARGETS.words}
                      </td>
                      <td>{row.status}</td>
                    </tr>
                  ))}
                  {historyRows.length === 0 && (
                    <tr>
                      <td colSpan={6}>No weeks yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>

        <footer className="contract-footer">
          <button type="button" onClick={() => navigate("/admin")}>
            Back to Admin
          </button>
        </footer>
      </div>
    </div>
  );
}
